import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, Landmark, Timer, KeyRound, Calculator, Briefcase, MessageSquare, ChevronRight } from "lucide-react";
import { GUIDES, CHARGES, guidePlace } from "@/data/guides";
import { ROADMAP_STAGES, RIGHTS, fplAnnual } from "@/data/legalData";
import { Seo, SITE_URL, breadcrumbs } from "@/components/Seo";
import { Button } from "@/components/ui/button";

const usd = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const Fact = ({ icon: Icon, label, text, id }) => (
  <div data-testid={`guide-fact-${id}`} className="rounded-2xl border border-parchment-200 bg-white p-6">
    <Icon className="h-5 w-5 text-terracotta" />
    <h3 className="mt-3 font-serif text-xl font-semibold text-forest-900">{label}</h3>
    <p className="mt-1.5 text-sm text-ink-600 leading-relaxed">{text}</p>
  </div>
);

const PdTable = ({ g }) => (
  <table data-testid="guide-pd-table" className="mt-5 w-full text-sm border-collapse">
    <thead>
      <tr className="text-left text-ink-500 border-b border-parchment-200">
        <th className="py-2 font-medium">Household size</th>
        <th className="py-2 font-medium">Likely qualify (≤125%)</th>
        <th className="py-2 font-medium">May qualify (≤200%)</th>
      </tr>
    </thead>
    <tbody>
      {[1, 2, 3, 4].map((n) => {
        const a = fplAnnual(g.name, n);
        return (
          <tr key={n} className="border-b border-parchment-100">
            <td className="py-2.5 text-forest-900">{n} {n === 1 ? "person" : "people"}</td>
            <td className="py-2.5">{usd(a * 1.25)}/yr <span className="text-ink-400">({usd((a * 1.25) / 12)}/mo)</span></td>
            <td className="py-2.5">{usd(a * 2)}/yr <span className="text-ink-400">({usd((a * 2) / 12)}/mo)</span></td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const Stages = ({ place }) => (
  <ol className="mt-6 space-y-4">
    {ROADMAP_STAGES.map((s) => (
      <li key={s.id} className="flex gap-4">
        <span className="shrink-0 grid place-items-center h-8 w-8 rounded-full bg-forest-800 text-parchment-50 font-mono text-xs">{s.n}</span>
        <div>
          <h3 className="font-semibold text-forest-900">{s.title}</h3>
          <p className="text-sm text-ink-600 leading-relaxed">{s.summary}</p>
          <p className="mt-1 text-sm text-ink-500"><strong className="text-ink-700">Defense tip{place ? ` in ${place}` : ""}:</strong> {s.defense[0]}</p>
        </div>
      </li>
    ))}
  </ol>
);

const Cta = ({ g }) => (
  <div className="grid sm:grid-cols-3 gap-3">
    <Link to="/eligibility" data-testid="guide-cta-eligibility"><Button variant="outline" className="w-full rounded-xl h-11 gap-2 border-parchment-200"><Calculator className="h-4 w-4" />Check eligibility</Button></Link>
    <Link to="/bail" data-testid="guide-cta-bail"><Button variant="outline" className="w-full rounded-xl h-11 gap-2 border-parchment-200"><KeyRound className="h-4 w-4" />Estimate bail</Button></Link>
    <Link to={`/attorneys?state=${encodeURIComponent(g.name)}`} data-testid="guide-cta-lawyer"><Button className="w-full rounded-xl h-11 gap-2 bg-forest-800 hover:bg-forest-900 text-parchment-50"><Briefcase className="h-4 w-4" />Find a lawyer</Button></Link>
  </div>
);

export default function GuideState() {
  const { slug } = useParams();
  const g = GUIDES.find((x) => x.slug === slug);
  if (!g) return <Navigate to="/guides" replace />;
  const place = guidePlace(g);
  const title = g.federal ? "Federal Criminal Case Guide: Arrest, Bail, Speedy Trial & Public Defenders"
    : `${g.name} Criminal Case Guide: Arrest, Bail, Speedy Trial & Public Defenders`;
  const description = `What happens after an arrest in ${place}: courts, speedy trial deadlines, bail and release rules, public defender income limits and the 7 stages of a criminal case. Free, plain-English guide.`;
  const path = `/guides/${g.slug}`;
  const others = GUIDES.filter((x) => x.slug !== g.slug);
  const jsonLd = { "@context": "https://schema.org", "@graph": [
    { "@type": "Article", headline: title, description, url: `${SITE_URL}${path}`, dateModified: "2026-06-01", inLanguage: "en-US",
      about: { "@type": "Place", name: g.federal ? "United States" : g.name },
      publisher: { "@type": "Organization", name: "SentinelGuide", logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.svg` } } },
    breadcrumbs([{ name: "Home", path: "/" }, { name: "State guides", path: "/guides" }, { name: g.federal ? "Federal" : g.name, path }]),
  ]};

  return (
    <article data-testid="guide-page" className="grain-bg min-h-screen">
      <Seo title={title} description={description} path={path} jsonLd={jsonLd} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-ink-500">
          <Link to="/" className="hover:text-terracotta">Home</Link><ChevronRight className="h-3 w-3" />
          <Link to="/guides" data-testid="guide-breadcrumb-guides" className="hover:text-terracotta">State guides</Link><ChevronRight className="h-3 w-3" />
          <span className="text-ink-700">{g.federal ? "Federal" : g.name}</span>
        </nav>
        <h1 data-testid="guide-title" className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-forest-900 leading-[1.08]">
          What happens after an arrest in {place}
        </h1>
        <p className="mt-5 text-ink-700 leading-relaxed">
          A plain-English overview of how criminal cases work in {place}: where your case is heard, how long the
          prosecution has to bring you to trial, how bail and release work, and whether you may qualify for a free lawyer.
        </p>

        <section className="mt-10 grid sm:grid-cols-3 gap-4">
          <Fact id="courts" icon={Landmark} label="Courts" text={g.court} />
          <Fact id="speedy" icon={Timer} label="Speedy trial" text={g.speedy} />
          <Fact id="bail" icon={KeyRound} label="Bail & release" text={g.bail} />
        </section>

        <section className="mt-14">
          <h2 className="font-serif text-3xl font-semibold text-forest-900">Do you qualify for a public defender in {place}?</h2>
          <p className="mt-3 text-ink-600 leading-relaxed">
            If you can't afford a lawyer, the Sixth Amendment guarantees you one when jail time is possible. Many courts
            use the federal poverty guidelines as a starting point. These 2025 estimates show common cut-offs — the court
            makes the final decision and may consider debts, assets and the cost of hiring a private lawyer.
          </p>
          <PdTable g={g} />
        </section>

        <section className="mt-14">
          <h2 className="font-serif text-3xl font-semibold text-forest-900">The 7 stages of a criminal case</h2>
          <Stages place={place} />
          <Link to="/roadmap" data-testid="guide-roadmap-link" className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-terracotta hover:underline">
            Explore the interactive roadmap <ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        <section className="mt-14">
          <h2 className="font-serif text-3xl font-semibold text-forest-900">Your rights at every stage</h2>
          <ul className="mt-5 grid sm:grid-cols-2 gap-4">
            {RIGHTS.slice(0, 4).map((r) => (
              <li key={r.title} className="rounded-2xl border border-parchment-200 bg-white p-5">
                <span className="font-mono text-[10px] uppercase tracking-wider text-terracotta">{r.statute}</span>
                <h3 className="mt-1 font-semibold text-forest-900">{r.title}</h3>
                <p className="mt-1 text-sm text-ink-600">{r.body}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14 rounded-3xl bg-forest-900 text-parchment-50 p-8">
          <h2 className="font-serif text-3xl font-semibold">Next steps</h2>
          <p className="mt-2 text-parchment-200/80 text-sm">Check your options, then talk to a defense lawyer who handles cases in {place}.</p>
          <div className="mt-6"><Cta g={g} /></div>
          <Link to="/assistant" data-testid="guide-cta-ai" className="mt-4 inline-flex items-center gap-2 text-sm text-parchment-100 hover:underline">
            <MessageSquare className="h-4 w-4" /> Ask the AI companion about your case
          </Link>
        </section>

        <p data-testid="guide-disclaimer" className="mt-10 text-xs text-ink-400 leading-relaxed">
          Last reviewed June 2026. General legal information only, not legal advice. Laws and court rules change and
          vary by county — confirm deadlines and options with a licensed attorney in {place}.
        </p>

        <section className="mt-12 pt-8 border-t border-parchment-200">
          <h2 className="font-serif text-xl font-semibold text-forest-900">Common charges {g.federal ? "explained" : `in ${g.name}`}</h2>
          <div data-testid="guide-charge-links" className="mt-4 flex flex-wrap gap-2">
            {CHARGES.map((c) => (
              <Link key={c.slug} to={g.federal ? `/charges/${c.slug}` : `/charges/${c.slug}/${g.slug}`} className="text-xs px-3 py-1.5 rounded-full border border-forest-800/30 bg-forest-100 text-forest-900 hover:border-forest-800 transition-colors">
                {c.name}
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-12 pt-8 border-t border-parchment-200">
          <h2 className="font-serif text-xl font-semibold text-forest-900">Guides for other states</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {others.map((o) => (
              <Link key={o.slug} to={`/guides/${o.slug}`} className="text-xs px-3 py-1.5 rounded-full border border-parchment-200 text-ink-600 hover:border-forest-800 hover:text-forest-900 transition-colors">
                {o.federal ? "Federal" : o.name}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </article>
  );
}
